import { __awaiter } from 'tslib';
import * as Long from 'long';
import { im_proto } from '../proto/index.js';
import { ApiManager } from './api/ApiManager.js';
import { SdkConsts, InitResult, IMEvent, WebSocketLevel, ErrorType, InitApiMode, MessageSource, FlightStatus, InfoKeys } from './consts.js';
import { BaseError } from './errors/BaseError.js';
import { EventBus } from './events/EventBus.js';
import { Logger } from './metrics/logger.js';
import { Conversation } from './model/Conversation.js';
import { Message } from './model/Message.js';
import { NetworkManager } from './network/NetworkManager.js';
import { applyDefaultBoe, applyDefault, optionCheck } from './options.js';
import { uuidv4 } from './utils/uuid.js';
import { Monitor, MonitorItem } from './metrics/monitor.js';
import { Ticker } from './utils/ticker.js';
import { RuntimeContext, ServiceIdentity } from './service/RuntimeContext.js';
import { ConnectionStatus, NetworkType } from '../adapter/abstract/IMNetApi.js';
import { ConversationManager } from './service/ConversationManager.js';
import { MessageManager } from './service/MessageManager.js';
import { ParticipantManager } from './service/ParticipantManager.js';
import { InboxType } from './service/InboxType.js';
import { AdapterManager } from '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import { AuthManager } from './network/AuthManager.js';

class BytedIMCore {
    /**
     * @param opt 初始化配置
     * @param plugins 插件
     * @example
     * ```ts
     * new BytedIM(option, [ExtensionPlugin, StrangerPlugin, MultimediaPlugin,EmojiPlugin,StoragePlugin,MetricsPlugin])
     * ```
     */
    constructor(opt, plugins) {
        /**
         *  im实例是否销毁
         */
        this.disposed = false;
        this.plugins = [];
        /**
         *  @private
         */
        this.innerCursor = [];
        /** @private */
        // @ts-ignore
        this.ticker = undefined;
        /** @private */
        this.isProcessing = false;
        const ctx = new RuntimeContext();
        this.ctx = ctx;
        ctx.register(ServiceIdentity.Monitor, Monitor);
        ctx.register(ServiceIdentity.CoreInstance, this);
        if (!opt.headers) {
            opt.headers = {};
        }
        if (!opt.httpHeaders) {
            opt.httpHeaders = {};
        }
        if (!opt.extended) {
            opt.extended = {};
        }
        if (opt.boe) {
            opt = applyDefaultBoe(opt);
            if (typeof opt.boe === 'string') {
                opt.headers[SdkConsts.envKey] = opt.boe;
                opt.headers[SdkConsts.boeHeaderKey] = '1';
                opt.httpHeaders[SdkConsts.envKey] = opt.boe;
                opt.httpHeaders[SdkConsts.boeHeaderKey] = '1';
            }
            if (typeof opt.boe === 'boolean') {
                opt.headers[SdkConsts.boeHeaderKey] = '1';
                opt.httpHeaders[SdkConsts.boeHeaderKey] = '1';
            }
        }
        else {
            opt = applyDefault(opt);
            if (typeof opt.ppe === 'string') {
                opt.headers[SdkConsts.envKey] = opt.ppe;
                opt.headers[SdkConsts.ppeHeaderKey] = '1';
                opt.httpHeaders[SdkConsts.envKey] = opt.ppe;
                opt.httpHeaders[SdkConsts.ppeHeaderKey] = '1';
            }
            else if (opt.canary) {
                opt.headers[SdkConsts.envKey] = 'canary';
                opt.httpHeaders[SdkConsts.envKey] = 'canary';
            }
        }
        ctx.option = opt;
        optionCheck(this.ctx, opt);
        // prepare adapter
        if (opt.adapter) {
            Logger.ctxDebug(this.ctx, 'install adapter:', opt.adapter);
            AdapterManager.setDynamicAdapter(opt.adapter);
        }
        else {
            this.initAdapter();
        }
        // register rest component
        ctx.register(ServiceIdentity.ConversationManager, ConversationManager);
        ctx.register(ServiceIdentity.MessageManager, MessageManager);
        ctx.register(ServiceIdentity.ParticipantManager, ParticipantManager);
        ctx.register(ServiceIdentity.EventBus, EventBus);
        ctx.register(ServiceIdentity.NetworkManager, NetworkManager);
        ctx.register(ServiceIdentity.CoreApi, ApiManager);
        ctx.register(ServiceIdentity.InboxType, InboxType);
        ctx.register(ServiceIdentity.AuthManager, AuthManager);
        if (opt.debug) {
            let globalObject = typeof window !== 'undefined' ? window : opt.injectContext;
            let ctxName = `__imsdk_context_${this.ctx.id.split('-')[0]}`;
            // 设置要注入的对象, 默认注入到 window
            if (typeof opt.injectContext === 'object') {
                globalObject = opt.injectContext;
                opt.injectContext = true;
            }
            // 设置要注入到的property name, 默认 '__imsdk_context'
            if (typeof opt.injectContext === 'string') {
                ctxName = opt.injectContext;
                opt.injectContext = true;
            }
            if (typeof opt.injectContext === 'undefined') {
                opt.injectContext = true;
            }
            if (typeof opt.injectContext === 'boolean') {
                if (opt.injectContext) {
                    try {
                        // 使用 Object.defineProperty 避免被框架搞
                        Object.defineProperty(globalObject, ctxName, {
                            enumerable: false,
                            configurable: true,
                            get() {
                                return ctx;
                            }
                        });
                    }
                    catch (err) {
                        Logger.ctxDebug(ctx, 'inject ctx:', ctx, `with name: ${ctxName} to`, globalObject, 'failed: ', err);
                    }
                }
            }
        }
        if (Array.isArray(plugins)) {
            plugins.forEach(Plugin => {
                const p = new Plugin(ctx);
                p.install();
                this.plugins.push(p);
            });
            this.ctx.plugin = this.plugins;
        }
        Logger.ctxDebug(this.ctx, 'loaded plugin:', this.plugins);
        if (opt.boe) {
            Logger.ctxDebug(this.ctx, `using boe env: ${opt.boe === true ? 'default' : opt.boe}`);
        }
        else if (opt.ppe) {
            Logger.ctxDebug(this.ctx, `using ppe env: ${opt.ppe}`);
        }
        this.network.onMessage.subscribe(p => this.receivePacket(p));
        if (Object.seal) {
            Object.seal(this);
            Object.seal(ctx);
            Object.seal(BytedIMCore.prototype);
        }
    }
    /**
     * 返回运行上下文
     * @private
     */
    getContext() {
        return this.ctx;
    }
    /**
     * sdk内部多实例调用解决方案
     *  @private
     */
    resolve(identity) {
        return this.ctx.resolve(identity);
    }
    /**
     *  初始化结果
     */
    get initResult() {
        return this.ctx.initResult;
    }
    set initResult(val) {
        this.ctx.initResult = val;
    }
    /**
     *  事件订阅中心
     */
    get event() {
        return this.resolve(ServiceIdentity.EventBus);
    }
    /**
     *  sdk内部使用的api
     *  @private
     */
    get api() {
        return this.resolve(ServiceIdentity.CoreApi);
    }
    /**
     *  sdk内部使用的网络应用
     *  @private
     */
    get network() {
        return this.resolve(ServiceIdentity.NetworkManager);
    }
    /**
     *  认证管理，包括token和证书
     */
    get auth() {
        return this.resolve(ServiceIdentity.AuthManager);
    }
    /**
     *  inboxType 信箱类型
     */
    get inboxType() {
        return this.resolve(ServiceIdentity.InboxType);
    }
    /**
     * 返回当前上下文id
     * @private
     */
    get id() {
        return this.ctx.id;
    }
    /**
     *  实例化im的参数
     */
    get option() {
        return this.ctx.option;
    }
    /**
     *  获取用户游标
     *  @private
     */
    getUserCursor(inboxType) {
        var _a;
        return (_a = this.innerCursor[inboxType !== null && inboxType !== void 0 ? inboxType : this.inboxType.getDefaultInbox()]) !== null && _a !== void 0 ? _a : Long.ZERO;
    }
    /**
     *  设置用户游标
     *  @private
     */
    setUserCursor(val, inboxType) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const inbox = inboxType !== null && inboxType !== void 0 ? inboxType : this.inboxType.getDefaultInbox();
            const current = this.innerCursor[inbox];
            if (!current || current.lt(val)) {
                Logger.ctxDebug(this.ctx, `update cursor for inbox:${inbox} from ${current === null || current === void 0 ? void 0 : current.toString()} to ${val.toString()}`);
                this.innerCursor[inbox] = val;
                yield ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.saveUserCursor(inbox, val));
            }
            else {
                return;
            }
        });
    }
    /**
     * sdk初始化，会完成与服务端的消息游标同步和历史信息同步 (可设置 {@link disableInitPull} 关闭历史信息同步、只同步用户游标)
     * @param params.convLimit 每次调用初始化拉取接口时，会话数量限制 ({@link disableInitPull} true 时不生效)
     * @param params.msgLimit 每次调用初始化拉取接口时，每个对话返回的消息数量限制 ({@link disableInitPull} true 时不生效)
     * @param params.mode 控制调用的初始化接口，默认为 CMD 203 {@link AwemeMode}
     * @param params.convTotal 初始化拉取的会话总数量限制，非0时，当本地大于等于该值时停止拉取
     */
    init(params) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = Monitor.performanceNow();
            this.initResult = InitResult.Start;
            const isUsingDb = (_b = (yield ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.init(this.ctx.option.userId)))) !== null && _b !== void 0 ? _b : false;
            try {
                yield this.auth.prepareToken();
            }
            catch (error) {
                this.initResult = InitResult.Error;
                Logger.ctxError(this.ctx, 'prepare token error:', error);
                this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizSdkInit + MonitorItem.ErrorSuffix, startTime, {
                    use_db: (_c = isUsingDb.toString()) !== null && _c !== void 0 ? _c : 'unknown',
                    reason: 'token'
                });
                return this.initResult;
            }
            for (const inbox of this.inboxType.getInboxTypeArray()) {
                const prepareResult = yield this.prepareHistoryForInbox({
                    inboxType: inbox,
                    convLimit: params === null || params === void 0 ? void 0 : params.convLimit,
                    msgLimit: params === null || params === void 0 ? void 0 : params.msgLimit,
                    mode: params === null || params === void 0 ? void 0 : params.mode,
                    convTotal: params === null || params === void 0 ? void 0 : params.convTotal
                });
                if (!prepareResult) {
                    this.initResult = InitResult.Error;
                    Logger.ctxError(this.ctx, 'init history error for inbox', inbox);
                    this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizSdkInit + MonitorItem.ErrorSuffix, startTime, {
                        use_db: (_d = isUsingDb.toString()) !== null && _d !== void 0 ? _d : 'unknown',
                        reason: 'history'
                    });
                    return this.initResult;
                }
            }
            yield this.resolve(ServiceIdentity.ConversationManager).refreshLocalAsync();
            this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.ConversationChange, this);
            try {
                // 无论有没有pull历史消息, 都需要初始化插件
                // 插件注入初始化
                for (const p of this.plugins) {
                    yield p.init();
                }
            }
            catch (error) {
                this.initResult = InitResult.Error;
                Logger.ctxError(this.ctx, 'init plugin error:', error);
                this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizSdkInit + MonitorItem.ErrorSuffix, startTime, {
                    use_db: (_e = isUsingDb.toString()) !== null && _e !== void 0 ? _e : 'unknown',
                    reason: 'plugin'
                });
                return this.initResult;
            }
            if (this.ctx.option.webSocketLevel !== WebSocketLevel.Disable) {
                try {
                    this.resolve(ServiceIdentity.Monitor).emitCounter(MonitorItem.WebSocketConnectFirst, 1, {
                        url: (_g = (_f = this.network.ws) === null || _f === void 0 ? void 0 : _f.url) !== null && _g !== void 0 ? _g : 'unknown'
                    });
                    yield this.network.connectWs(true);
                }
                catch (error) {
                    Logger.ctxWarn(this.ctx, 'skip websocket, init open fail:', error);
                }
            }
            const tickInterval = this.ctx.option.pullInterval === false || this.ctx.option.pullInterval === undefined ?
                30000 :
                this.ctx.option.pullInterval;
            Logger.ctxDebug(this.ctx, 'use ticker interval:', tickInterval);
            this.ticker = new Ticker(this.ctx, tickInterval);
            this.ticker.onTick.subscribe(() => {
                void this.tickerUpdate();
            });
            this.ticker.restart();
            this.initResult = InitResult.Succeeded;
            this.resolve(ServiceIdentity.Monitor).emitDuration(MonitorItem.BizSdkInit + MonitorItem.SuccessSuffix, startTime, {
                use_db: (_h = isUsingDb.toString()) !== null && _h !== void 0 ? _h : 'unknown'
            });
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.InitFinish, this, this.initResult);
            return this.initResult;
        });
    }
    // TODO: 50005 + ByUser 的边界问题
    /**
     * sdk内部初始化时拉取历史消息 业务一般不使用
     * @param params.inboxType 参见 {@link init}
     * @param params.convLimit 参见 {@link init}
     * @param params.msgLimit 参见 {@link init}
     * @param params.mode 参见 {@link init}
     *
     * @param params.cursor default init (CMD 203) 分页参数
     * @param params.initSubType default init (CMD 203) 分页参数
     *
     * @param params.page {@link AwemeMode} init (CMD 2043) 分页参数
     * @param params.version {@link AwemeMode} init (CMD 2043) 分页参数
     *
     * @return 返回下一次请求的参数，下一次请求时，直接透传入参即可
     * @private
     */
    getMessagesByUserInit(params = {}) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            const inboxType = (_a = params.inboxType) !== null && _a !== void 0 ? _a : this.inboxType.getDefaultInbox();
            const queryCursor = (_b = params.cursor) !== null && _b !== void 0 ? _b : Long.ZERO;
            const queryPage = params.page;
            if (!this.inboxType.isValidInbox(inboxType)) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'invalid inbox',
                    type: ErrorType.InvalidInboxType,
                    sender: this
                });
            }
            // 发起请求
            let conversations;
            let messages;
            let perUserCursor;
            let hasMore;
            let nextQueryCursor = Long.ZERO;
            let version = Long.ZERO;
            let logid;
            if (params.mode === InitApiMode.AwemeMode) {
                const response = yield this.api.GetMessagesByInit({
                    inboxType,
                    convLimit: params.convLimit,
                    msgLimit: params.msgLimit,
                    page: queryPage,
                    version: params.version
                });
                const resp = (_c = response.body) === null || _c === void 0 ? void 0 : _c.message_by_init;
                conversations = (_d = resp.messages) === null || _d === void 0 ? void 0 : _d.map(msg => msg.conversations);
                messages = [];
                (_e = resp.messages) === null || _e === void 0 ? void 0 : _e.forEach(msg => msg.messages && (messages === null || messages === void 0 ? void 0 : messages.push(...msg.messages)));
                perUserCursor = resp === null || resp === void 0 ? void 0 : resp.user_cursor;
                hasMore = resp.has_more;
                version = resp.next_init_version;
                logid = response.log_id;
            }
            else {
                const response = yield this.api.GetMessagesByUserInitV2({
                    cursor: Long.fromValue(queryCursor),
                    inboxType,
                    initSubType: params.initSubType,
                    convLimit: params.convLimit,
                    msgLimit: params.msgLimit
                });
                const resp = (_f = response.body) === null || _f === void 0 ? void 0 : _f.messages_per_user_init_v2_body;
                conversations = resp.conversations;
                messages = resp.messages;
                perUserCursor = resp === null || resp === void 0 ? void 0 : resp.per_user_cursor;
                hasMore = resp.has_more;
                nextQueryCursor = resp.next_cursor;
                logid = response.log_id;
            }
            // 处理请求结果
            this.processInitConversation(conversations, logid);
            yield this.processInitMessage(messages, logid);
            // 如果前后两次的cursor不同, 拉一次混链补偿消息
            if (this.getUserCursor(inboxType).neq(Long.ZERO) &&
                (perUserCursor === null || perUserCursor === void 0 ? void 0 : perUserCursor.neq(Long.ZERO)) &&
                ((_g = this.getUserCursor(inboxType)) === null || _g === void 0 ? void 0 : _g.neq(perUserCursor))) {
                Logger.ctxWarn(this.ctx, `cursor not match: before:${(_h = this.getUserCursor(inboxType)) === null || _h === void 0 ? void 0 : _h.toString()}, after:${perUserCursor === null || perUserCursor === void 0 ? void 0 : perUserCursor.toString()}, pull user, logid:${logid}`);
                yield this.getMessagesByUser({ inboxType });
            }
            // 写入拉取到的cursor
            if (perUserCursor) {
                yield this.setUserCursor(perUserCursor, inboxType);
            }
            this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.InitLoadPage, this);
            return {
                hasMore,
                cursor: nextQueryCursor,
                page: (queryPage !== null && queryPage !== void 0 ? queryPage : 0) + 1,
                version
            };
        });
    }
    processInitMessage(messages, logid) {
        return __awaiter(this, void 0, void 0, function* () {
            if (messages) {
                yield this.resolve(ServiceIdentity.MessageManager).processNewMessagesFromPull(messages, MessageSource.Init, logid);
            }
        });
    }
    processInitConversation(conversations, logid) {
        if (conversations) {
            conversations
                .map(c => Conversation.fromServerConversation(this.ctx, c, logid))
                .forEach(c => this.resolve(ServiceIdentity.ConversationManager).upsert(c));
        }
    }
    /**
     * 拉取用户(混链)消息，通常不需要业务调用，sdk内部会轮询
     * @param params.cursor 拉取消息位置游标
     * @param params.limit 单次拉取消息数量
     * @param params.inboxType 信箱类型
     */
    getMessagesByUser(params = {}) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.cursor instanceof Date) {
                params.cursor = `${params.cursor.getTime()}000`;
            }
            if (params.inboxType === undefined) {
                params.inboxType = this.inboxType.getDefaultInbox();
            }
            if (!this.inboxType.isValidInbox(params.inboxType)) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'invalid inbox',
                    type: ErrorType.InvalidInboxType,
                    sender: this
                });
            }
            // 阻止没有拉取历史消息的情况下从0开始拉混链
            if (this.ctx.option.disableInitPull === true &&
                this.getConversationList().length === 0 &&
                this.getUserCursor(params.inboxType).eq(Long.ZERO) &&
                params.cursor === undefined) {
                Logger.ctxWarn(this.ctx, 'try to pull history from 0, preventing');
                return;
            }
            let hasMore = true;
            let cursor = params.cursor !== undefined ? Long.fromValue(params.cursor) : this.getUserCursor(params.inboxType);
            while (hasMore) {
                const response = yield this.api.GetMessagesByUser({
                    cursor,
                    limit: (_a = params.limit) !== null && _a !== void 0 ? _a : 50,
                    inboxType: params.inboxType
                });
                const resp = (_b = response.body) === null || _b === void 0 ? void 0 : _b.messages_per_user_body;
                hasMore = resp.has_more;
                cursor = resp.next_cursor;
                const conLastIndexMap = new Map();
                (_c = resp.hasmore_message_conv_list) === null || _c === void 0 ? void 0 : _c.forEach((item) => {
                    const conversationId = item.toString();
                    const con = this.resolve(ServiceIdentity.ConversationManager).getWithShortIdRaw(conversationId);
                    conLastIndexMap.set(conversationId, con === null || con === void 0 ? void 0 : con.lastMessageIndex);
                });
                // 如果下面两步失败了会导致 userCursor 写不进去
                const { conMap } = yield this.resolve(ServiceIdentity.MessageManager).processNewMessagesFromPull(resp.messages, MessageSource.UserInbox, response.log_id);
                (_d = resp.hasmore_message_conv_list) === null || _d === void 0 ? void 0 : _d.forEach((item) => {
                    const conversationId = item.toString();
                    const minMessage = conMap.get(conversationId);
                    if (minMessage) {
                        const con = this.resolve(ServiceIdentity.ConversationManager).getRaw(minMessage.conversationId);
                        void this.patchMessage({
                            cursor: minMessage,
                            conversation: con,
                            limitindex: conLastIndexMap.get(conversationId)
                        });
                    }
                    else {
                        Logger.ctxWarn(this.ctx, `not found message of conversation in hasmore_message_conv_list, conversationid:${conversationId}, logid:${response.log_id}`);
                    }
                });
                if (this.initResult === InitResult.Start) {
                    this.resolve(ServiceIdentity.EventBus).emitEmpty(IMEvent.InitLoadPage, this);
                }
            }
            yield this.setUserCursor(cursor, params.inboxType);
            this.resolve(ServiceIdentity.ConversationManager).refreshLocal();
        });
    }
    /**
     * 请求服务器获取会话内的消息列表
     * @param params.conversation 会话
     * @param params.cursor 游标
     * @param params.direction 拉取方向
     * @param params.limit 单次拉取消息数量
     * @param params.limitIndex 无用参数，可忽略
     */
    getMessagesByConversation(params) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            let { cursor } = params;
            const { direction } = params;
            if (cursor instanceof Message) {
                cursor = cursor.indexInConversation;
            }
            if (cursor === undefined) {
                cursor = params.conversation.firstMessageIndex;
                // next cursor may less than internal cursor
                if (params.conversation.firstMessageIndex.gt((_a = params.conversation.__internal_pullCursor) !== null && _a !== void 0 ? _a : Long.ZERO)) {
                    cursor = params.conversation.__internal_pullCursor;
                    Logger.ctxDebug(this.ctx, `using internal cursor: ${cursor.toString()} < ${params.conversation.firstMessageIndex.toString()} for conversation:`, params.conversation);
                }
            }
            const response = yield this.api.GetMessagesByConversation({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                anchorIndex: Long.fromValue(cursor),
                direction: direction ? direction : im_proto.MessageDirection.OLDER,
                limit: (_b = params.limit) !== null && _b !== void 0 ? _b : 20,
                inboxType: params.conversation.inboxType
            });
            this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.GetMessagesByConversation, {
                count: 1
            }, {
                log_id: response.log_id,
                from: 'user',
                conversation_id: params.conversation.shortId,
                cursor: Long.fromValue(cursor).toString()
            });
            const resp = (_c = response.body) === null || _c === void 0 ? void 0 : _c.messages_in_conversation_body;
            params.conversation.__internal_pullCursor = (_d = resp.next_cursor) !== null && _d !== void 0 ? _d : params.conversation.__internal_pullCursor;
            const { msgs: messages } = yield this.resolve(ServiceIdentity.MessageManager).processNewMessagesFromPull(resp.messages, MessageSource.LoadMore, response.log_id);
            return {
                messages,
                hasMore: resp === null || resp === void 0 ? void 0 : resp.has_more,
                cursor: resp === null || resp === void 0 ? void 0 : resp.next_cursor
            };
        });
    }
    /**
     * 标记会话已读
     * @param params.conversation 会话
     * @param params.readIndex 消息的indexInConversation 默认会话的lastMessageIndex（最后一条消息的index）
     * @param params.convUnreadCount 会话未读数
     * @param params.totalUnreadCount 总未读数
     */
    markConversationRead(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let { readIndex } = params;
            if (readIndex === undefined) {
                readIndex = params.conversation.lastMessageIndex;
            }
            else if (readIndex instanceof Message) {
                readIndex = readIndex.indexInConversation;
            }
            // prevent mark a smaller number
            if (Long.fromValue(readIndex).lte(params.conversation.readIndex)) {
                return;
            }
            this.resolve(ServiceIdentity.ConversationManager).markRead(params.conversation.id, Long.fromValue(readIndex));
            let convUnreadCount;
            let totalUnreadCount;
            if (this.ctx.option.unreadCountReport) {
                const allConvsUnread = this.getConversationList({
                    filter: c => c.inboxType === params.conversation.inboxType && c.isMember && c.mode === 0
                }).reduce((total, current) => total + current.unreadCount, 0);
                totalUnreadCount = (_a = params.totalUnreadCount) !== null && _a !== void 0 ? _a : allConvsUnread;
                convUnreadCount = (_b = params.convUnreadCount) !== null && _b !== void 0 ? _b : params.conversation.unreadCount;
            }
            yield this.api.MarkConversationReadV3({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                readIndex: Long.fromValue(readIndex),
                inboxType: params.conversation.inboxType,
                unreadCount: convUnreadCount !== undefined ? Long.fromValue(convUnreadCount) : undefined,
                totalUnreadCount: totalUnreadCount !== undefined ? Long.fromValue(totalUnreadCount) : undefined
            });
        });
    }
    /**
     * 撤回消息
     * @param params.message 撤回的消息
     */
    recallMessage(params) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!params.message.serverId) {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.MessageNotReady,
                    msg: `message ${params.message} is not ready`,
                    sender: this
                });
            }
            const conv = this.resolve(ServiceIdentity.ConversationManager).get(params.message.conversationId);
            const body = yield this.api.RecallMessage({
                conversationId: params.message.conversationId,
                conversationShortId: Long.fromString(params.message.conversationShortId),
                conversationType: params.message.conversationType,
                serverId: Long.fromString(params.message.serverId),
                inboxType: conv.inboxType
            });
            this.resolve(ServiceIdentity.MessageManager).markRecalled(params.message.conversationId, params.message.serverId, body.log_id);
        });
    }
    /**
     * 创建会话
     * @param params.participants 会话内的用户id
     * @param params.bizExt biz拓展信息
     * @param params.inboxType 信箱类型
     * @param params.type 会话类型
     * @param params.persistent 持久化幂等，默认true，不建议业务修改
     * @param params.idempotentId 幂等id 由业务方自己生成，以同样的幂等 id 创建群聊会话会返回之前已经创建好的群聊会话
     * @param params.name 群名称
     * @param params.avatarUrl 群头像
     * @param params.desc 群描述
     */
    createConversation(params) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const startTime = Monitor.performanceNow();
            let participants = [];
            const result = {
                success: false,
                payload: null
            };
            if (params.inboxType === undefined) {
                params.inboxType = this.inboxType.getDefaultInbox();
            }
            if (!this.inboxType.isValidInbox(params.inboxType)) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'invalid inbox',
                    type: ErrorType.InvalidInboxType,
                    sender: this
                });
            }
            if (params.type === undefined) {
                if (Array.isArray(params.participants)) {
                    // 两个人的群聊场景少, 显式指定一下, 默认创建单聊
                    // 如果传空的成员, 认为是尝试创建自己和自己的会话, 也是个单聊
                    if (params.participants.length <= 1) {
                        params.type = im_proto.ConversationType.ONE_TO_ONE_CHAT;
                    }
                    else {
                        params.type = im_proto.ConversationType.GROUP_CHAT;
                    }
                }
                else {
                    // 只有一个 participant 一定是单聊
                    params.type = im_proto.ConversationType.ONE_TO_ONE_CHAT;
                }
            }
            if (Array.isArray(params.participants)) {
                if (params.participants.indexOf(this.ctx.option.userId) === -1) {
                    participants = params.participants.concat(this.ctx.option.userId);
                }
                else {
                    participants = params.participants;
                }
            }
            else {
                participants = [params.participants, this.ctx.option.userId];
            }
            if (participants.length > 2 && params.type === im_proto.ConversationType.ONE_TO_ONE_CHAT) {
                result.statusCode = ErrorType.InvalidParam;
                result.statusMsg = 'one to one chat can only has 2 participants';
                return result;
            }
            if (params.persistent === undefined && params.idempotentId !== undefined && params.idempotentId.length > 0) {
                params.persistent = true;
            }
            try {
                const response = yield this.api.CreateConversationV2({
                    type: params.type,
                    participants: participants.map(p => Long.fromValue(p)),
                    persistent: params.persistent,
                    idempotentId: params.idempotentId,
                    name: params.name,
                    avatarUrl: params.avatarUrl,
                    desc: params.desc,
                    bizExt: params.bizExt,
                    inboxType: params.inboxType
                });
                const resp = response.body.create_conversation_v2_body;
                result.checkCode = resp.check_code;
                result.checkMsg = resp.check_message;
                result.statusCode = resp.status;
                result.statusMsg = resp.extra_info;
                result.body = resp;
                if ((resp === null || resp === void 0 ? void 0 : resp.status) === 0) {
                    const c = Conversation.fromServerConversation(this.ctx, resp === null || resp === void 0 ? void 0 : resp.conversation, response.log_id);
                    this.resolve(ServiceIdentity.ConversationManager).upsert(c);
                    yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(c);
                    result.success = true;
                    result.payload = c;
                }
            }
            catch (err) {
                result.statusCode = (_a = err.type) !== null && _a !== void 0 ? _a : ErrorType.NetworkError;
                result.innerError = err;
            }
            this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.CreateConversation, {
                create_cost: Monitor.performanceNow() - startTime
            }, {
                type: (_c = (_b = params.type) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : 'unknown',
                error_code: (_e = (_d = result.statusCode) === null || _d === void 0 ? void 0 : _d.toString()) !== null && _e !== void 0 ? _e : 'unknown'
            });
            return result;
        });
    }
    /**
     * 根据conversationId获取指定会话（本地）
     * @param params.conversationId 会话id
     */
    getConversation(params) {
        return this.resolve(ServiceIdentity.ConversationManager).get(params.conversationId);
    }
    /**
     * 根据conversationId获取指定会话（server） 请求服务端更新本地会话后返回
     * @param params.conversationId 会话id
     * @param params.shortId shortId
     * @param params.type 会话类型
     */
    getConversationOnline(params) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.resolve(ServiceIdentity.ConversationManager).getWithOnline(params.conversationId, params.shortId, params.type);
        });
    }
    /**
     * 获取会话列表（本地）
     * @param params.filter 过滤条件 默认是过滤掉陌生人会话和空会话
     */
    getConversationList(params = {}) {
        return this.resolve(ServiceIdentity.ConversationManager).getConversationArray(params.filter);
    }
    /**
     * 获取会话列表（server）异步方法,会扫描一遍所有的本地会话，再从服务端拉取那些信息不完整的会话，最后返回本地的会话数组
     * @param params.filter 过滤条件 默认是过滤掉陌生人会话和空会话
     */
    getConversationListOnline(params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.resolve(ServiceIdentity.ConversationManager).refreshLocalAsync();
            return this.getConversationList({
                filter: params.filter
            });
        });
    }
    /**
     * 创建消息
     * @param params.conversation 当前发送消息的会话
     * @param params.type 消息类型
     * @param params.content 消息体
     * @param params.clientId 消息客户端id
     * @param params.ext 拓展属性
     * @param params.mentionedUsers 被艾特的永用户列表
     * @param params.insert 消息创建后是否process处理(新消息处理流程)
     * @param params.referenceMessage 引用的消息
     * @param params.referenceHint 自定义引用线索 传入自己想传入的任意信息
     */
    createMessage(params) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (params.insert === undefined) {
                params.insert = true;
            }
            const ext = Object.assign({}, params.ext);
            const message = Message.createClientMessage(this.ctx, {
                type: params.type,
                content: params.content,
                ext,
                id: (_a = params.clientId) !== null && _a !== void 0 ? _a : uuidv4(),
                conversationId: params.conversation.id,
                mentionedUsers: params.mentionedUsers || [],
                conversationShortId: params.conversation.shortId,
                conversationType: params.conversation.type,
                referenceMessage: params.referenceMessage,
                referenceHint: params.referenceHint
            });
            message.flightStatus = FlightStatus.Created;
            message.sendFunc = this.__internal_sendMessageObject.bind(this);
            message.indexInConversation = params.conversation.lastMessageIndex.add(1);
            message.orderInConversation = params.conversation.lastMessageOrder.add(1);
            if (params.insert) {
                yield this.resolve(ServiceIdentity.MessageManager).processNewMessage(message, MessageSource.Offline);
            }
            return message;
        });
    }
    /**
     * 发送消息 用户任何消息类型
     * @param params.message 要发送的消息
     */
    sendMessage(params) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const { message } = params;
            const startTime = Date.now();
            const result = yield message.sendFunc(message);
            this.resolve(ServiceIdentity.EventBus).emit(IMEvent.MessageSend, this, message);
            try {
                const endTime = Date.now();
                const conversation = this.resolve(ServiceIdentity.ConversationManager).get(message.conversationId);
                this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.SendMessage, {
                    con_member_count: conversation.participantCount - 1,
                    send_cost_time: endTime - startTime
                }, {
                    con_type: message.conversationType.toString(),
                    conversation_id: message.conversationId,
                    msg_uuid: message.serverId,
                    msg_type: message.type.toString(),
                    send_start_time: startTime.toString(),
                    send_end_time: endTime.toString(),
                    error_code: (_b = (_a = result.statusCode) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : 'unknown',
                    is_ws: ((_c = this.network.ws) === null || _c === void 0 ? void 0 : _c.isOpen()) ? '1' : '0',
                    logid: (_d = result.logid) !== null && _d !== void 0 ? _d : ''
                });
            }
            catch (err) {
                Logger.ctxWarn(this.ctx, 'Fail to report data after send message', err);
            }
            return result;
        });
    }
    /**
     * 获取指定会话
     * @param params.participantId 拉取单聊
     * @param params.inboxType 信箱类型
     * @param params.shortId 拉取已知 conversation short id 的群聊
     * @param params.idempotentId 拉取已知幂等ID的群聊
     */
    fetchConversation(params) {
        return __awaiter(this, void 0, void 0, function* () {
            let localConv = null;
            if (params.shortId !== undefined) {
                const findLocalConv = this.resolve(ServiceIdentity.ConversationManager).getWithShortIdRaw(params.shortId);
                if (findLocalConv !== null && !findLocalConv.isOffline) {
                    return findLocalConv;
                }
            }
            if (params.inboxType === undefined) {
                params.inboxType = this.inboxType.getDefaultInbox();
            }
            if (!this.inboxType.isValidInbox(params.inboxType)) {
                throw new BaseError({
                    ctx: this.ctx,
                    msg: 'invalid inbox',
                    type: ErrorType.InvalidInboxType,
                    sender: this
                });
            }
            if (params.idempotentId !== undefined && params.participantId === undefined) {
                // 群聊 幂等建群
                const createResult = yield this.createConversation({
                    type: im_proto.ConversationType.GROUP_CHAT,
                    participants: [],
                    inboxType: params.inboxType,
                    idempotentId: params.idempotentId
                });
                if (createResult.success) {
                    localConv = createResult.payload;
                    if (localConv !== null) {
                        yield this.getConversationOnline({
                            conversationId: localConv.id,
                            shortId: localConv.shortId,
                            type: im_proto.ConversationType.ONE_TO_ONE_CHAT
                        });
                    }
                }
            }
            else if (params.participantId !== undefined) {
                // 单聊 创建会话
                const createResult = yield this.createConversation({
                    type: im_proto.ConversationType.ONE_TO_ONE_CHAT,
                    participants: params.participantId,
                    inboxType: params.inboxType
                });
                if (createResult.success) {
                    localConv = createResult.payload;
                    if (localConv !== null) {
                        // 从服务端拉一次, 补足数据
                        yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(localConv);
                    }
                }
            }
            else if (params.shortId !== undefined) {
                // 群聊 直接提供 short id 拉会话
                localConv = yield this.getConversationOnline({
                    conversationId: params.shortId,
                    shortId: params.shortId,
                    type: im_proto.ConversationType.GROUP_CHAT
                });
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.InvalidParam,
                    msg: 'no valid param provided',
                    reachServer: false,
                    sender: this
                });
            }
            if (localConv !== null && !localConv.isOffline) {
                yield this.getMessagesByConversation({
                    conversation: localConv
                });
                return localConv;
            }
            else {
                throw new BaseError({
                    ctx: this.ctx,
                    type: ErrorType.ConversationNotExist,
                    msg: 'fetch failed, conv is null or offline',
                    reachServer: false,
                    sender: this
                });
            }
        });
    }
    /**
     * 手动卸载sdk
     */
    dispose() {
        var _a, _b;
        // 移除所有事件钩子
        this.resolve(ServiceIdentity.EventBus).unsubscribeAll();
        // 停止轮询
        (_a = this.ticker) === null || _a === void 0 ? void 0 : _a.stop();
        // 关闭websocket
        this.network.closeWs();
        (_b = this.network.ws) === null || _b === void 0 ? void 0 : _b.dispose();
        this.network.onMessage.unsubscribeAll();
        for (const plugin of this.plugins) {
            void plugin.dispose();
        }
        // 卸载插件
        this.plugins.forEach(p => p.dispose());
        this.plugins.length = 0;
        // 释放数据存储
        this.resolve(ServiceIdentity.ConversationManager).dispose();
        this.resolve(ServiceIdentity.MessageManager).dispose();
        // 卸载 Monitor
        this.ctx.option.monitor = undefined;
        // 设置一个切面, 打error log
        // 如果有metrics插件
        this.ctx.option.aspectBefore = () => {
            Logger.ctxError(this.ctx, 'do not invoke a disposed instance');
            return false;
        };
        this.disposed = true;
        Logger.ctxDebug(this.ctx, 'sdk unloaded, do not invoke this instance');
    }
    initAdapter() {
        // impl by sub class
    }
    /**
     * 初始化历史消息 sdk内部使用
     * @param inboxType 参见 {@link init}
     * @param convLimit 参见 {@link init}
     * @param msgLimit 参见 {@link init}
     * @param mode 参见 {@link init}
     * @private
     */
    prepareHistoryForInbox({ inboxType, convLimit, msgLimit, mode, convTotal }) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const userCursor = yield ((_a = this.resolve(ServiceIdentity.DbProxy)) === null || _a === void 0 ? void 0 : _a.loadUserCursor(inboxType));
                // 如果读到了cursor, 那么认为数据会被加载, 忽略load history
                if (userCursor !== undefined) {
                    Logger.ctxDebug(this.ctx, `load history for inbox ${inboxType} by db`);
                    yield this.setUserCursor(userCursor, inboxType);
                    yield this.getMessagesByUser({
                        inboxType,
                        cursor: userCursor
                    });
                    return true;
                }
                // 设置了不拉取历史消息, 则只拉个cursor
                if (this.ctx.option.disableInitPull) {
                    Logger.ctxDebug(this.ctx, `load history for inbox ${inboxType} by disable init`);
                    let user_cursor;
                    if (mode === InitApiMode.AwemeMode) {
                        const response = yield this.api.GetMessagesByInit({
                            inboxType,
                            convLimit,
                            msgLimit
                        });
                        const resp = (_b = response.body) === null || _b === void 0 ? void 0 : _b.message_by_init;
                        user_cursor = resp.user_cursor;
                    }
                    else {
                        const response = yield this.api.GetMessagesByUserInitV2({
                            inboxType,
                            convLimit,
                            msgLimit,
                            cursor: Long.ZERO
                        });
                        const resp = (_c = response.body) === null || _c === void 0 ? void 0 : _c.messages_per_user_init_v2_body;
                        user_cursor = resp.per_user_cursor;
                    }
                    yield this.setUserCursor(user_cursor, inboxType);
                    return true;
                }
                // 拉取完整的历史数据
                Logger.ctxDebug(this.ctx, `load history for inbox ${inboxType} by full init`);
                let nextParams = {
                    hasMore: true,
                    cursor: Long.ZERO,
                    page: 0,
                    version: undefined
                };
                while (nextParams.hasMore) {
                    nextParams = yield this.getMessagesByUserInit(Object.assign({ inboxType, initSubType: this.ctx.option.initSubType, convLimit,
                        msgLimit,
                        mode }, nextParams));
                    if (convTotal &&
                        this.resolve(ServiceIdentity.ConversationManager).getConversationArray().length >= convTotal) {
                        break;
                    }
                }
                return true;
            }
            catch (err) {
                Logger.ctxError(this.ctx, `load history failed for inbox:${inboxType}`, err);
                return false;
            }
        });
    }
    /**
     * 通过会话远程拉取消息
     * @private
     * @param params.conversation 会话
     */
    patchMessage(params) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            let { cursor } = params;
            const DEFAULT_LIMIT = 20;
            if (!params.limitindex) {
                // 不存在边界 不补偿
                return;
            }
            if (cursor instanceof Message) {
                cursor = cursor.indexInConversation;
            }
            if (cursor === undefined) {
                cursor = params.conversation.firstMessageIndex;
            }
            const response = yield this.api.GetMessagesByConversation({
                conversationId: params.conversation.id,
                conversationShortId: Long.fromString(params.conversation.shortId),
                conversationType: params.conversation.type,
                anchorIndex: Long.fromValue(cursor),
                direction: im_proto.MessageDirection.OLDER,
                limit: (_a = params.limit) !== null && _a !== void 0 ? _a : DEFAULT_LIMIT,
                inboxType: params.conversation.inboxType
            });
            this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.GetMessagesByConversation, {
                count: 1
            }, {
                log_id: response.log_id,
                from: 'patch',
                conversationId: params.conversation.shortId,
                cursor: Long.fromValue(cursor).toString()
            });
            const resp = (_b = response.body) === null || _b === void 0 ? void 0 : _b.messages_in_conversation_body;
            const { conMap } = yield this.resolve(ServiceIdentity.MessageManager).processNewMessagesFromPull(resp.messages, MessageSource.UserInbox, response.log_id);
            const minMessage = conMap.get(params.conversation.shortId);
            const hasMore = resp === null || resp === void 0 ? void 0 : resp.has_more;
            if (!hasMore || !minMessage || params.limitindex.gt((minMessage === null || minMessage === void 0 ? void 0 : minMessage.indexInConversation) || Long.ZERO)) {
                params.conversation.forceRefreshUnreadCount();
                return;
            }
            void this.patchMessage({
                conversation: params.conversation,
                limit: params.limit || DEFAULT_LIMIT,
                cursor: resp === null || resp === void 0 ? void 0 : resp.next_cursor,
                limitindex: params.limitindex
            });
        });
    }
    /**
     * sdk内部轮询
     * @private
     */
    tickerUpdate() {
        return __awaiter(this, void 0, void 0, function* () {
            if ((yield this.network.net.getConnectionStatus()) === ConnectionStatus.Disconnected) {
                return;
            }
            if (this.initResult !== InitResult.Succeeded) {
                return;
            }
            // if pull is disabled or processing push message, ignore pulling
            if (this.ctx.option.pullInterval && !this.isProcessing) {
                for (const inbox of this.inboxType.getInboxTypeArray()) {
                    try {
                        yield this.getMessagesByUser({
                            inboxType: inbox
                        });
                        this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.GetMessagesByTicker, {
                            count: 1
                        }, {
                            tick_timer: this.ticker.getTickTimer(),
                            cursor: this.getUserCursor(inbox).toString(),
                            time: Math.round(Date.now() / 100).toString(),
                            inbox: inbox.toString()
                        });
                    }
                    catch (err) {
                        Logger.ctxWarn(this.ctx, 'ticker running in pull user err:', err);
                    }
                }
            }
            // emit plugin ticker
            for (const plugin of this.plugins) {
                try {
                    yield plugin.ticker();
                }
                catch (err) {
                    Logger.ctxWarn(this.ctx, 'ticker running in plugin err:', err);
                }
            }
        });
    }
    /**
     * sdk内部使用发送消息
     * @internal
     */
    __internal_sendMessageObject(message) {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            const result = {
                success: false,
                payload: message
            };
            // 已经发送成功的消息不能再发
            if (message.serverId) {
                result.statusCode = 0;
                return result;
            }
            const c = this.resolve(ServiceIdentity.ConversationManager).getRaw(message.conversationId);
            if (c === undefined) {
                result.statusCode = ErrorType.ConversationNotExist;
                result.statusMsg = `conversation ${message.conversationId} not exist in local`;
                return result;
            }
            message.flightStatus = FlightStatus.Inflight;
            yield this.resolve(ServiceIdentity.MessageManager).processNewMessage(message, MessageSource.Offline);
            try {
                if (!c.ticket) {
                    yield this.resolve(ServiceIdentity.ConversationManager).refreshTicket(c.id);
                }
            }
            catch (err) {
                message.flightStatus = FlightStatus.Failed;
                result.statusCode = (_a = err.type) !== null && _a !== void 0 ? _a : ErrorType.InvalidTicket;
                result.innerError = err;
                this.resolve(ServiceIdentity.MessageManager).upsert(message);
                return result;
            }
            try {
                const response = yield this.api.SendMessage({
                    conversationType: c.type,
                    conversationId: c.id,
                    conversationShortId: Long.fromString(c.shortId),
                    content: message.content,
                    ticket: c.ticket,
                    ext: message.ext,
                    messageType: message.type,
                    clientId: message.clientId,
                    mentionedUsers: message.mentionedUsers.map(u => Long.fromString(u)),
                    inboxType: c.inboxType,
                    referenceInfo: message.referenceInfo
                });
                const res = (_b = response.body) === null || _b === void 0 ? void 0 : _b.send_message_body;
                // 不管 status 是多少, 都往消息里写入这些字段
                message.ext[InfoKeys.SendResponseCheckCode] = (_c = res === null || res === void 0 ? void 0 : res.check_code.toString()) !== null && _c !== void 0 ? _c : '';
                message.ext[InfoKeys.SendResponseCheckMessage] = res === null || res === void 0 ? void 0 : res.check_message;
                message.ext[InfoKeys.SendResponseExtraInfo] = res === null || res === void 0 ? void 0 : res.extra_info;
                message.ext[InfoKeys.SendResponseStatus] = (_d = res === null || res === void 0 ? void 0 : res.status.toString()) !== null && _d !== void 0 ? _d : '';
                if (this.ctx.option.debug) {
                    message.ext[InfoKeys.LocalLogId] = response.log_id;
                }
                result.body = res;
                result.checkCode = res.check_code;
                result.checkMsg = res.check_message;
                result.statusCode = res.status;
                result.statusMsg = res.extra_info;
                result.logid = response.log_id;
                // 发送异常的处理
                if (res.status === 0) {
                    const serverId = res.server_message_id.toString();
                    message.serverId = serverId;
                    message.flightStatus = FlightStatus.Succeeded;
                    message.isOffline = false;
                    yield this.resolve(ServiceIdentity.MessageManager).processNewMessage(message, MessageSource.Offline);
                    result.success = true;
                }
                else {
                    // 消息送到了服务端, 但可能被拒绝了
                    // @ts-ignore 处理报错但是实际发送成功的情况
                    if (message.flightStatus !== FlightStatus.Received) {
                        message.flightStatus = FlightStatus.Rejected;
                        if (res.status === im_proto.SendMessageStatus.CHECK_MSG_NOT_PASS_BUT_SELF_VISIBLE) {
                            message.flightStatus = FlightStatus.SelfVisible;
                        }
                    }
                    else {
                        // 虽然报错了, 但是收到了 push, 所以认为是发送成功
                        result.success = true;
                        // 不需要 process, 消息始终是最新的
                    }
                }
            }
            catch (err) {
                // @ts-ignore
                if (message.flightStatus !== FlightStatus.Received) {
                    message.flightStatus = FlightStatus.Failed;
                }
                result.innerError = err;
                result.statusCode = (_e = err.type) !== null && _e !== void 0 ? _e : ErrorType.NetworkError;
            }
            this.resolve(ServiceIdentity.MessageManager).upsert(message);
            return result;
        });
    }
    /**
     * 新消息处理
     * @private
     */
    receivePacket(res) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return __awaiter(this, void 0, void 0, function* () {
            // 防止冲突, 加个锁
            // FIXME: 锁的逻辑调整
            this.isProcessing = true;
            if (this.initResult !== InitResult.Succeeded) {
                return;
            }
            let needPull = false;
            const oldCursor = this.getUserCursor(res.inbox_type);
            if (res.cmd === im_proto.IMCMD.NEW_MSG_NOTIFY) {
                const body = (_a = res.body) === null || _a === void 0 ? void 0 : _a.has_new_message_notify;
                if (body) {
                    const conv = this.resolve(ServiceIdentity.ConversationManager).getRaw(body.conversation_id);
                    yield this.clientAck({
                        packet: res,
                        inboxType: (_b = conv === null || conv === void 0 ? void 0 : conv.inboxType) !== null && _b !== void 0 ? _b : res === null || res === void 0 ? void 0 : res.inbox_type,
                        type: im_proto.MsgReportType.MSG_RECEIVE_BY_WS
                    });
                }
                if (!body) {
                    needPull = true;
                    Logger.ctxWarn(this.ctx, `push body is missing, logid:${res.log_id}`);
                }
                else if (oldCursor.lt((_c = body.previous_cursor) !== null && _c !== void 0 ? _c : Long.ZERO) || ((_d = body.previous_cursor) === null || _d === void 0 ? void 0 : _d.eq(Long.ZERO))) {
                    // switch to pull mode, which means mesasage lost or large message
                    // drop push packet
                    needPull = true;
                    Logger.ctxWarn(this.ctx, `push cursour is greater than local cursor, push:${(_e = body.previous_cursor) !== null && _e !== void 0 ? _e : Long.ZERO}, local:${oldCursor.toString()}, msgid:${(_f = body.message) === null || _f === void 0 ? void 0 : _f.server_message_id}, logid:${res.log_id}`);
                }
                else if ((_g = body.previous_cursor) === null || _g === void 0 ? void 0 : _g.eq(oldCursor)) {
                    // handle normally
                    const msg = body.message;
                    if (msg) {
                        this.reportMessageDelayTime(msg, res.log_id);
                        const m = Message.fromServerMessage(this.ctx, msg, res.log_id);
                        if (!m.ext) {
                            m.ext = {};
                        }
                        if (this.ctx.option.debug) {
                            m.ext[InfoKeys.LocalLogId] = res.log_id;
                        }
                        yield this.resolve(ServiceIdentity.MessageManager).processNewMessage(m, MessageSource.Online);
                        yield this.setUserCursor(body.next_cursor, res.inbox_type);
                    }
                    else {
                        Logger.ctxWarn(this.ctx, 'msg body is empty:', body);
                    }
                }
                else {
                    needPull = true;
                    Logger.ctxWarn(this.ctx, `push cursour is less than local cursor, push:${body.previous_cursor}, local:${oldCursor}, msgid:${(_h = body.message) === null || _h === void 0 ? void 0 : _h.server_message_id}, logid:${res.log_id}`);
                }
            }
            if (needPull) {
                yield this.getMessagesByUser({
                    // 总会拉到最新, 可以忽略 push 下来的消息
                    inboxType: res.inbox_type,
                    cursor: oldCursor
                });
            }
            this.resolve(ServiceIdentity.ConversationManager).refreshLocal();
            for (const p of this.plugins) {
                yield p.receivePacket(res);
            }
            this.isProcessing = false;
        });
    }
    /**
     * 校验ack
     * @private
     */
    clientAck({ packet, inboxType, type }) {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            if (!((_a = packet.body) === null || _a === void 0 ? void 0 : _a.has_new_message_notify) || !packet.start_time_stamp || packet.start_time_stamp.lte(0)) {
                return;
            }
            const currentNetwork = yield this.network.net.getNetworkType();
            let protoNetwork;
            switch (currentNetwork) {
                case NetworkType.Cellular_2G:
                    protoNetwork = im_proto.NetworkType.MOBILE_2G;
                    break;
                case NetworkType.Cellular_3G:
                    protoNetwork = im_proto.NetworkType.MOBILE_3G;
                    break;
                case NetworkType.Cellular_4G:
                    protoNetwork = im_proto.NetworkType.MOBILE_4G;
                    break;
                case NetworkType.Cellular_5G:
                    protoNetwork = im_proto.NetworkType.MOBILE_5G;
                    break;
                case NetworkType.Wifi:
                    protoNetwork = im_proto.NetworkType.WIFI;
                    break;
                default:
                    protoNetwork = im_proto.NetworkType.UNKNOWN;
                    break;
            }
            this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.MessageAck, {
                count: 1
            }, {
                start_timestamp: packet.start_time_stamp.toString(),
                cmd: packet.cmd.toString(),
                network_type: protoNetwork.toString(),
                logid: packet.log_id,
                client_timestamp: new Date().getTime().toString(),
                server_message_id: (_d = (_c = (_b = packet.body.has_new_message_notify.message) === null || _b === void 0 ? void 0 : _b.server_message_id) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '0',
                report_net_type: type.toString()
            });
            yield this.api.ClientAck({
                startTimeStamp: packet.start_time_stamp,
                cmd: packet.cmd,
                NetworkType: protoNetwork,
                logid: packet.log_id,
                clientTimeStamp: Long.fromNumber(new Date().getTime()),
                serverId: (_f = (_e = packet.body.has_new_message_notify.message) === null || _e === void 0 ? void 0 : _e.server_message_id) !== null && _f !== void 0 ? _f : Long.ZERO,
                inboxType,
                type
            });
        });
    }
    /**
     * 上报消息接收延迟时间
     * @param msg 消息
     * @param logid logid
     * @returns void
     * @private
     */
    reportMessageDelayTime(msg, logid) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const createTime = (_a = msg.create_time) === null || _a === void 0 ? void 0 : _a.toNumber();
        if (!createTime) {
            return;
        }
        const timeDelta = this.ctx.option.timeCalibration ? this.resolve(ServiceIdentity.Monitor).avgDelta : 0;
        const current = Date.now();
        const duration = current + timeDelta - createTime;
        if (duration <= 0) {
            Logger.ctxDebug(this.ctx, `message reception delay is less than 0, serverId:${msg.server_message_id}, current:${current}, createTime:${createTime}, timeDelta:${timeDelta}`);
            return;
        }
        this.resolve(ServiceIdentity.Monitor).emitMetrics(MonitorItem.ReceiveMessage, {
            recieve_cost_time: duration
        }, {
            con_type: (_c = (_b = msg.conversation_type) === null || _b === void 0 ? void 0 : _b.toString()) !== null && _c !== void 0 ? _c : '',
            conversation_id: (_d = msg.conversation_id) !== null && _d !== void 0 ? _d : '',
            msg_uuid: (_f = (_e = msg.server_message_id) === null || _e === void 0 ? void 0 : _e.toString()) !== null && _f !== void 0 ? _f : '',
            recieve_end_time: current.toString(),
            recieve_start_time: createTime.toString(),
            time_delta: timeDelta.toString(),
            msg_type: (_h = (_g = msg.message_type) === null || _g === void 0 ? void 0 : _g.toString()) !== null && _h !== void 0 ? _h : '',
            error_code: '0',
            ntp_ready: this.ctx.option.timeCalibration ? '1' : '0',
            is_ws: '1',
            logid
        });
    }
}

export { BytedIMCore };
//# sourceMappingURL=BytedIM.js.map
